import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Info, TriangleAlert } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import type { FailureMode } from '../../types/drbfm';
import { buildDocumentUrl, getHighlightStyle } from './drawing-evidence/evidenceImage';
import { drawingEvidenceLabel } from './decision-memory/decisionMemoryText';
import { useDrawingEvidenceImage } from './drawing-evidence/useDrawingEvidenceImage';

interface DRBFMDrawingEvidenceDialogProps {
  open: boolean;
  failureMode: FailureMode | null;
  onClose: () => void;
}

const DRBFMDrawingEvidenceDialog: React.FC<DRBFMDrawingEvidenceDialogProps> = ({
  open,
  failureMode,
  onClose,
}) => {
  const { t } = useTranslation();
  const evidence = failureMode?.drawingEvidence;
  const { imageUrl, loading, error } = useDrawingEvidenceImage(evidence, open);

  const fallbackUrl = evidence ? buildDocumentUrl(evidence) : undefined;
  const highlightStyle = useMemo(
    () => (evidence ? getHighlightStyle(evidence) : null),
    [evidence],
  );

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* The inset keeps the MUI dialog's viewport gutter, which `w-full` alone drops. */}
      <DialogContent
        className="revy-ui grid-rows-[auto_minmax(0,1fr)_auto] gap-0 max-h-[calc(100vh-4rem)] w-[calc(100%-2rem)] p-0 sm:max-w-[1200px]"
        aria-describedby={undefined}
      >
        <DialogHeader className="p-6">
          <DialogTitle className="revy-cjk text-lg leading-[1.5]">
            {drawingEvidenceLabel(t, evidence)}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto border-y p-6">
          {!evidence && (
            <Alert variant="info">
              <Info strokeWidth={1.9} />
              <AlertDescription className="revy-cjk leading-[1.6]">
                {t('drbfm.drawingEvidence.notLinked', '図面根拠はまだ紐付いていません。')}
              </AlertDescription>
            </Alert>
          )}

          {evidence && (
            <div className="flex flex-col gap-4">
              <div>
                <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
                  {evidence.reasoning || failureMode?.failureDescription}
                </p>
                {fallbackUrl && (
                  <a
                    href={fallbackUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="revy-cjk text-sm leading-[1.5] text-primary underline-offset-4 hover:underline"
                  >
                    {t('drbfm.drawingEvidence.openDocumentPage', '文書ページを開く')}
                  </a>
                )}
              </div>

              {loading && (
                <div className="flex justify-center py-16">
                  <Spinner />
                </div>
              )}

              {error && (
                <Alert variant="warning">
                  <TriangleAlert strokeWidth={1.9} />
                  <AlertDescription className="revy-cjk flex flex-wrap items-center justify-between gap-2 leading-[1.6]">
                    <span>{error}</span>
                    {fallbackUrl && (
                      <Button asChild variant="ghost" size="sm">
                        <a href={fallbackUrl} target="_blank" rel="noreferrer">
                          <span className="revy-cjk">
                            {t('drbfm.drawingEvidence.goToDocument', '文書へ')}
                          </span>
                          <ExternalLink strokeWidth={1.9} />
                        </a>
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {imageUrl && (
                <div className="max-h-[72vh] w-full overflow-auto border bg-muted">
                  <div className="relative inline-block min-w-full">
                    <img src={imageUrl} alt={drawingEvidenceLabel(t, evidence)} className="block h-auto w-full" />
                    {highlightStyle && (
                      <div data-testid="drawing-evidence-highlight" style={highlightStyle} />
                    )}
                  </div>
                </div>
              )}

              {imageUrl && !highlightStyle && (
                <Alert variant="info">
                  <Info strokeWidth={1.9} />
                  <AlertDescription className="revy-cjk leading-[1.6]">
                    {t(
                      'drbfm.drawingEvidence.missingNormalizedBbox',
                      '正規化されたbboxがないため、文書ページで根拠箇所を確認してください。',
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="p-6">
          <Button variant="ghost" onClick={onClose}>
            <span className="revy-cjk">{t('common.close', '閉じる')}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DRBFMDrawingEvidenceDialog;
